import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaArrowRight, FaCalendarAlt, FaClock } from 'react-icons/fa';
import { HeaderContext } from '../context/HeaderContext';

const HomeBlog = () => {
  const { blogs = [], imageUrl = '' } = useContext(HeaderContext);

  return (
    <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-4xl mx-auto"
        >
          <div className="inline-flex items-center px-4 py-2 bg-green-100 text-green-800 rounded-full text-sm font-medium mb-4">
            <FaCalendarAlt className="mr-2" />
            Latest Updates
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            Insights & <span className="text-green-600">Articles</span>
          </h2>
          <p className="text-xl text-gray-600">
            Discover valuable resources and expert perspectives from our team
          </p>
        </motion.div>

        {/* Blog Posts */}
        {blogs.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
            {blogs.slice(0, 3).map((post, index) => (
              <motion.article
                key={post.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                whileHover={{ y: -10 }}
                className="group relative bg-white rounded-2xl shadow-xl overflow-hidden transition-all duration-300 hover:shadow-2xl"
              >
                {/* Featured Image */}
                <Link to={`/blog/${post.slug}`} className="block">
                  <div className="relative h-60 overflow-hidden">
                    {post.image ? (
                      <motion.img
                        src={`${imageUrl}/${post.image}`}
                        alt={post.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        initial={{ scale: 1 }}
                        whileHover={{ scale: 1.1 }}
                        onError={(e) => {
                          e.target.src = '/default-blog-image.jpg';
                          e.onerror = null;
                        }}
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-r from-green-400 to-blue-500 flex items-center justify-center text-white text-2xl font-bold">
                        {post.title.substring(0, 2)}
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/10 to-transparent"></div>
                    <div className="absolute top-4 right-4 bg-white text-green-700 text-xs font-bold px-3 py-1 rounded-full shadow">
                      {post.category || 'General'}
                    </div>
                  </div>
                </Link>

                {/* Content */}
                <div className="p-6">
                  <div className="flex items-center text-sm text-gray-500 mb-3">
                    <span className="flex items-center">
                      <FaCalendarAlt className="mr-1 text-green-500" />
                      {new Date(post.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </span>
                    <span className="mx-2">•</span>
                    <span className="flex items-center">
                      <FaClock className="mr-1 text-green-500" />
                      {post.read_time || '5'} min read
                    </span>
                  </div>

                  <Link to={`/blog/${post.slug}`}>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-green-600 transition-colors duration-300 line-clamp-2">
                      {post.title}
                    </h3>
                  </Link>

                  <p className="text-gray-600 mb-5 line-clamp-3">
                    {post.content?.replace(/<[^>]+>/g, '').substring(0, 150) || 'No content available'}...
                  </p>

                  <Link
                    to={`/blog/${post.slug}`}
                    className="inline-flex items-center text-green-600 font-medium hover:text-green-700 transition-colors group/readmore"
                  >
                    Continue Reading
                    <motion.span
                      initial={{ x: 0 }}
                      whileHover={{ x: 5 }}
                      transition={{ duration: 0.3 }}
                      className="ml-2"
                    >
                      <FaArrowRight className="transition-all duration-300 group-hover/readmore:translate-x-1" />
                    </motion.span>
                  </Link>
                </div>

                {/* Hover effect overlay */}
                <div className="absolute inset-0 border-2 border-transparent group-hover:border-green-400 rounded-2xl pointer-events-none transition-all duration-300 m-1"></div>
              </motion.article>
            ))}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16 bg-white rounded-2xl shadow-sm"
          >
            <div className="inline-block p-4 bg-green-100 rounded-full mb-4">
              <svg className="w-12 h-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">No Articles Yet</h3>
            <p className="text-gray-600 max-w-md mx-auto">
              We're currently working on some great content. Check back soon!
            </p>
          </motion.div>
        )}

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center mt-16"
        >
          <Link
            to="/blog"
            className="inline-flex items-center justify-center bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-bold py-4 px-8 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            Explore All Articles
            <FaArrowRight className="ml-2 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default HomeBlog;